import Vue from 'vue'
import { Message, Notification } from 'element-ui'
import {
  epolicyMatchRematch,
  epolicyMatchRematchStatus,
  epolicyMatchRematchCancel,
} from '@/api/api'

const POLL_INTERVAL = 3000

const state = Vue.observable({
  running: false,
  runId: null,
  progress: {
    processed: 0,
    total: 0,
    matched: 0,
    percent: 0,
  },
})

let pollTimer = null
const completeListeners = new Set()

function applyProgress(d) {
  state.progress = {
    processed: d.processedCount != null ? d.processedCount : 0,
    total: d.totalCount != null ? d.totalCount : 0,
    matched: d.matchedCount != null ? d.matchedCount : 0,
    percent: d.progressPercent != null ? d.progressPercent : 0,
  }
}

function notifyRunning() {
  Notification.info({
    title: '政策匹配进行中',
    message: '任务在后台执行，您可继续浏览其他页面，完成后请刷新匹配结果页查看。',
    duration: 5000,
    position: 'bottom-right',
  })
}

function buildCompleteMessage(d) {
  if (d.message) return d.message
  const matched = d.matchedCount != null ? d.matchedCount : 0
  const total = d.totalCount != null ? d.totalCount : 0
  if (total === 0) return '暂无已解析政策，请先完成政策采集与解析'
  return `匹配完成，共推荐 ${matched} 条`
}

function notifyComplete(d) {
  const msg = buildCompleteMessage(d)
  if (d.status === 'failed') {
    Message.error(msg)
    return
  }
  if (d.status === 'interrupted') {
    Message.warning(msg)
    return
  }
  if (d.status !== 'running') {
    Notification.success({
      title: '政策匹配完成',
      message: `${msg}，请前往「精准匹配」页刷新查看结果。`,
      duration: 8000,
    })
  }
}

function handleComplete(d) {
  stopPoll()
  state.running = false
  state.runId = null
  notifyComplete(d)
  completeListeners.forEach((fn) => {
    try {
      fn(d)
    } catch (e) {
      // ignore listener errors
    }
  })
}

function stopPoll() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

async function pollOnce() {
  const params = state.runId ? { runId: state.runId } : {}
  const res = await epolicyMatchRematchStatus(params)
  const d = (res && res.data) || {}
  if (d.runId) state.runId = d.runId
  applyProgress(d)
  if (!d.running) {
    handleComplete(d)
    return true
  }
  return false
}

function startPoll() {
  stopPoll()
  const tick = async () => {
    if (!state.running) return true
    try {
      return await pollOnce()
    } catch (e) {
      handleComplete({ status: 'failed', message: '匹配进度查询失败' })
      return true
    }
  }
  tick().then((done) => {
    if (done || !state.running) return
    pollTimer = setInterval(async () => {
      if (await tick()) stopPoll()
    }, POLL_INTERVAL)
  })
}

function attachRunningRun(d, options = {}) {
  state.running = true
  state.runId = d.runId
  applyProgress(d)
  startPoll()
  if (options.notify !== false) notifyRunning()
}

async function resume() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token')
  if (!token || state.running) return
  try {
    const res = await epolicyMatchRematchStatus({})
    const d = (res && res.data) || {}
    if (d.running) attachRunningRun(d, { notify: true })
  } catch (e) {
    // ignore
  }
}

async function startRematch() {
  if (state.running) {
    return { ok: false, reason: 'running' }
  }
  try {
    const res = await epolicyMatchRematch()
    const d = (res && res.data) || {}
    if (d.status === 'running') {
      attachRunningRun(d, { notify: true })
      return { ok: true, started: true, data: d }
    }
    handleComplete(d)
    return { ok: true, started: false, data: d }
  } catch (e) {
    return { ok: false, reason: 'error' }
  }
}

async function cancelRematch() {
  if (!state.running) return
  try {
    await epolicyMatchRematchCancel({ runId: state.runId })
    const res = await epolicyMatchRematchStatus(state.runId ? { runId: state.runId } : {})
    handleComplete((res && res.data) || { status: 'interrupted', message: '已取消匹配' })
  } catch (e) {
    stopPoll()
    state.running = false
    state.runId = null
  }
}

function onComplete(fn) {
  completeListeners.add(fn)
  return () => completeListeners.delete(fn)
}

export default {
  state,
  resume,
  startRematch,
  cancelRematch,
  onComplete,
  stopPoll,
}
